import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import {
  anonymizePdf,
  AnonymizeSummary,
  anonymizeTable,
  AnonymizerOptions,
  deanonymize,
  downloadBlob,
  fetchAnonymizerOptions,
  inspectTable,
  TableInspection,
} from '../../api/batch3Api';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Anonymize: upload an Excel, CSV or text-based PDF file. Names are replaced with fake ones and structured data (emails, tax ids…) with reversible tokens.',
  'Download the ZIP. It holds the anonymized file, the name mapping (keep it safe), a report and a backup of the original.',
  'Deanonymize: upload the anonymized file with the NAME_MAPPING file (and PATTERN_MAPPING if patterns were masked) to restore it.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

type Tab = 'anonymize' | 'deanonymize';
type Status = 'idle' | 'loading' | 'success' | 'error';

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';
const textareaClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground';

const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function DataAnonymizerPanel({ mission, onBack }: PanelProps) {
  const [tab, setTab] = useState<Tab>('anonymize');
  const [options, setOptions] = useState<AnonymizerOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // shared anonymize settings
  const [nameColumnsText, setNameColumnsText] = useState('');
  const [excludeText, setExcludeText] = useState('');
  const [locale, setLocale] = useState('en_US');
  const [seed, setSeed] = useState('42');
  const [enabledPatterns, setEnabledPatterns] = useState<string[]>([]);

  // anonymize
  const [file, setFile] = useState<File[]>([]);
  const [inspection, setInspection] = useState<TableInspection | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [namesText, setNamesText] = useState('');
  const [columnTermsText, setColumnTermsText] = useState('');
  const [bandWidth, setBandWidth] = useState(220);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ filename: string; summary: AnonymizeSummary | null } | null>(null);

  // deanonymize
  const [mappingFile, setMappingFile] = useState<File[]>([]);
  const [dataFile, setDataFile] = useState<File[]>([]);
  const [patternFile, setPatternFile] = useState<File[]>([]);
  const [deStatus, setDeStatus] = useState<Status>('idle');
  const [deError, setDeError] = useState<string | null>(null);
  const [deResult, setDeResult] = useState<{ filename: string; names: number; tokens: number } | null>(null);

  const isPdf = file[0]?.name.toLowerCase().endsWith('.pdf') ?? false;

  useEffect(() => {
    fetchAnonymizerOptions()
      .then((opts) => {
        setOptions(opts);
        setNameColumnsText(opts.default_name_columns.join('\n'));
        setEnabledPatterns(opts.patterns.map((p) => p.key));
      })
      .catch((err) => setOptionsError(err instanceof Error ? err.message : 'Could not load options.'));
  }, []);

  // Inspect a new spreadsheet/CSV: detect name columns and sensitive patterns.
  useEffect(() => {
    setInspection(null);
    setResult(null);
    setError(null);
    setStatus('idle');
    if (!file[0] || isPdf) return;
    let cancelled = false;
    inspectTable(file[0], lines(nameColumnsText))
      .then((data) => {
        if (cancelled) return;
        setInspection(data);
        setSelectedColumns(data.detected_columns);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not read the file.');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // The name-column keywords are applied when the file is chosen; editing them later
    // does not re-read the file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const toggle = (list: string[], value: string, on: boolean) =>
    on ? [...list.filter((v) => v !== value), value] : list.filter((v) => v !== value);

  const seedNumber = Math.max(1, parseInt(seed, 10) || 42);

  const handleAnonymize = async () => {
    if (!file[0]) return;
    const common = { excludeNames: lines(excludeText), locale, seed: seedNumber, enabledPatterns };
    if (!isPdf && selectedColumns.length === 0) {
      setError('Select at least one column to anonymize.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    setResult(null);
    try {
      const { blob, filename, summary } = isPdf
        ? await anonymizePdf(file[0], {
            ...common,
            names: lines(namesText),
            columnTerms: lines(columnTermsText),
            columnBandWidth: bandWidth,
          })
        : await anonymizeTable(file[0], {
            ...common,
            nameColumns: lines(nameColumnsText),
            selectedColumns,
          });
      downloadBlob(blob, filename);
      setResult({ filename, summary });
      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anonymization failed.');
      setStatus('error');
    }
  };

  const handleDeanonymize = async () => {
    if (!mappingFile[0] || !dataFile[0]) {
      setDeError('Upload both the mapping file and the anonymized file.');
      setDeStatus('error');
      return;
    }
    setDeStatus('loading');
    setDeError(null);
    setDeResult(null);
    try {
      const { blob, filename, summary } = await deanonymize(dataFile[0], mappingFile[0], patternFile[0] ?? null);
      downloadBlob(blob, filename);
      setDeResult({ filename, names: summary?.names ?? 0, tokens: summary?.pattern_tokens ?? 0 });
      setDeStatus('success');
    } catch (err) {
      setDeError(err instanceof Error ? err.message : 'Deanonymization failed.');
      setDeStatus('error');
    }
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 flex gap-2">
        <TabButton active={tab === 'anonymize'} onClick={() => setTab('anonymize')}>
          🔒 Anonymize
        </TabButton>
        <TabButton active={tab === 'deanonymize'} onClick={() => setTab('deanonymize')}>
          🔓 Deanonymize
        </TabButton>
      </div>

      {optionsError && <ErrorNotice message={optionsError} />}

      {tab === 'anonymize' && options && (
        <>
          <details className="mb-5 rounded-lg border border-border bg-muted px-4 py-3">
            <summary className="cursor-pointer text-sm font-semibold text-foreground">⚙️ Settings</summary>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="da-cols" className="mb-1.5">
                  Name columns (one per line)
                </Label>
                <textarea
                  id="da-cols"
                  rows={6}
                  className={textareaClass}
                  value={nameColumnsText}
                  onChange={(e) => setNameColumnsText(e.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">Applied when you choose a file.</p>
              </div>
              <div>
                <Label htmlFor="da-excl" className="mb-1.5">
                  Names to keep unchanged
                </Label>
                <textarea
                  id="da-excl"
                  rows={6}
                  className={textareaClass}
                  placeholder={'CEO John Smith\nSystem Administrator'}
                  value={excludeText}
                  onChange={(e) => setExcludeText(e.target.value)}
                />
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="da-locale" className="mb-1.5">
                    Name locale
                  </Label>
                  <select id="da-locale" className={selectClass} value={locale} onChange={(e) => setLocale(e.target.value)}>
                    {options.locales.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="da-seed" className="mb-1.5">
                    Random seed
                  </Label>
                  <Input id="da-seed" type="number" min={1} value={seed} onChange={(e) => setSeed(e.target.value)} />
                  <p className="mt-1 text-xs text-muted-foreground">Change for a different set of fake names.</p>
                </div>
              </div>
            </div>
            <p className="mb-2 mt-4 text-sm font-semibold text-foreground">
              🔍 Sensitive pattern masking (reversible tokens)
            </p>
            <div className="grid gap-1 sm:grid-cols-3">
              {options.patterns.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={enabledPatterns.includes(p.key)}
                    onChange={(e) => setEnabledPatterns((cur) => toggle(cur, p.key, e.target.checked))}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </details>

          <FileDrop
            label="File to anonymize"
            note="Excel, CSV, or a text-based PDF."
            required
            accept=".xlsx,.xls,.csv,.pdf"
            hint="XLSX, XLS, CSV or PDF"
            accentColor={mission.color}
            files={file}
            onFiles={setFile}
          />

          {inspection && (
            <div className="mb-5 space-y-3 rounded-lg border border-border bg-muted p-4">
              <p className="text-sm text-foreground">
                ✅ {inspection.rows.toLocaleString()} rows · {inspection.columns.length} columns
              </p>
              <div>
                <p className="mb-1 text-sm font-semibold text-foreground">Columns to anonymize</p>
                <div className="grid gap-1 sm:grid-cols-3">
                  {inspection.columns.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(c)}
                        onChange={(e) => setSelectedColumns((cur) => toggle(cur, c, e.target.checked))}
                      />
                      {c}
                    </label>
                  ))}
                </div>
                {inspection.detected_columns.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    No name columns detected. Tick the columns to anonymize, or adjust the keywords in Settings.
                  </p>
                )}
              </div>
              {enabledPatterns.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-foreground">🔍 Patterns detected</p>
                  {Object.entries(inspection.patterns).filter(([p]) => enabledPatterns.includes(p)).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No sensitive patterns detected in this file.</p>
                  ) : (
                    <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                      {Object.entries(inspection.patterns)
                        .filter(([p]) => enabledPatterns.includes(p))
                        .map(([p, info]) => (
                          <li key={p}>
                            <strong>{info.label}</strong>: {info.count} match(es) in {info.columns.join(', ')}
                            {info.samples[0] ? ` (e.g. ${info.samples[0]})` : ''}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {isPdf && (
            <div className="mb-5 space-y-4 rounded-lg border border-border bg-muted p-4">
              <p className="text-sm text-muted-foreground">
                PDF mode: provide the names to anonymize. Structured patterns can also be masked.
              </p>
              <div>
                <Label htmlFor="da-names" className="mb-1.5">
                  Names to anonymize in the PDF (one per line)
                </Label>
                <textarea
                  id="da-names"
                  rows={4}
                  className={textareaClass}
                  placeholder={'John Smith\nJane Doe'}
                  value={namesText}
                  onChange={(e) => setNamesText(e.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">Only exact text matches are replaced.</p>
              </div>
              <div>
                <Label htmlFor="da-terms" className="mb-1.5">
                  Column / field labels to mask completely (one per line)
                </Label>
                <textarea
                  id="da-terms"
                  rows={3}
                  className={textareaClass}
                  placeholder={'Employee Name\nPAYE Ref\nTax Code'}
                  value={columnTermsText}
                  onChange={(e) => setColumnTermsText(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="da-band" className="mb-1.5">
                  Column mask width: {bandWidth}
                </Label>
                <input
                  id="da-band"
                  type="range"
                  min={80}
                  max={500}
                  step={10}
                  value={bandWidth}
                  onChange={(e) => setBandWidth(Number(e.target.value))}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Increase if the full table column is not covered; decrease if neighbouring columns get masked.
                </p>
              </div>
            </div>
          )}

          <Button
            onClick={handleAnonymize}
            disabled={status === 'loading' || !file[0]}
            style={{ backgroundColor: mission.color }}
            className="w-full text-white hover:opacity-90"
          >
            {status === 'loading' ? 'Anonymizing…' : isPdf ? '🔒 Anonymize PDF' : '🔒 Anonymize Names'}
          </Button>
          {status === 'loading' && <ProcessingNotice label={'Anonymizing…'} />}

          {status === 'error' && error && <ErrorNotice message={error} />}
          {status === 'success' && result && (
            <SuccessNotice>
              <p>✅ {result.filename} downloaded. Keep the NAME_MAPPING file safe: it is the only way to restore the data.</p>
              {result.summary && (
                <div className="mt-2 text-xs text-green-800">
                  <p>
                    {result.summary.names_changed} of {result.summary.names_total} names changed ·{' '}
                    {result.summary.pattern_tokens} pattern token(s)
                    {result.summary.replacements !== undefined && ` · ${result.summary.replacements} PDF replacement(s)`}
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {result.summary.files.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </SuccessNotice>
          )}
          {status === 'idle' && !file[0] && <InfoNotice>👆 Upload a file to get started.</InfoNotice>}
        </>
      )}

      {tab === 'deanonymize' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <FileDrop
              label="Name mapping file"
              note="The NAME_MAPPING_*.xlsx from anonymization."
              required
              accept=".xlsx"
              hint="XLSX"
              accentColor={mission.color}
              files={mappingFile}
              onFiles={setMappingFile}
            />
            <FileDrop
              label="Anonymized file"
              note="Excel, CSV, Word (.docx) or PDF."
              required
              accept=".xlsx,.xls,.csv,.docx,.pdf"
              hint="XLSX, XLS, CSV, DOCX or PDF"
              accentColor={mission.color}
              files={dataFile}
              onFiles={setDataFile}
            />
          </div>
          <FileDrop
            label="Pattern mapping file (optional)"
            note="Only needed if patterns were masked: the PATTERN_MAPPING_*.xlsx."
            accept=".xlsx"
            hint="XLSX"
            accentColor={mission.color}
            files={patternFile}
            onFiles={setPatternFile}
          />
          <Button
            onClick={handleDeanonymize}
            disabled={deStatus === 'loading'}
            style={{ backgroundColor: mission.color }}
            className="w-full text-white hover:opacity-90"
          >
            {deStatus === 'loading' ? 'Restoring…' : '🔓 Deanonymize'}
          </Button>
          {deStatus === 'loading' && <ProcessingNotice label={'Restoring…'} />}
          {deStatus === 'error' && deError && <ErrorNotice message={deError} />}
          {deStatus === 'success' && deResult && (
            <SuccessNotice>
              <p>✅ {deResult.filename} downloaded.</p>
              <p className="mt-1 text-xs text-green-800">
                {deResult.names} name pair(s) loaded · {deResult.tokens} pattern token(s) restored.
              </p>
            </SuccessNotice>
          )}
          {deStatus === 'idle' && <InfoNotice>👆 Upload the mapping file and the anonymized file to proceed.</InfoNotice>}
        </>
      )}
    </MissionShell>
  );
}

export default DataAnonymizerPanel;
